import { useEffect, useRef, useCallback } from 'react';
import { Canvas, PencilBrush, Circle, Line, FabricObject } from 'fabric';
import { useAnnotationCanvas } from '@/context/annotation-canvas.context';

export function AnnotationCanvas() {
  const {
    isActive,
    activeTool,
    color,
    setActive,
    canvasElRef,
    registerClearHandler,
    registerUndoHandler,
  } = useAnnotationCanvas();

  const containerRef = useRef<HTMLDivElement>(null);
  const fabricRef = useRef<Canvas | null>(null);
  const isDrawingShapeRef = useRef(false);
  const shapeStartRef = useRef<{ x: number; y: number } | null>(null);
  const activeShapeRef = useRef<FabricObject | null>(null);
  const arrowGroupsRef = useRef<Map<FabricObject, FabricObject[]>>(new Map());

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const canvasEl = document.createElement('canvas');
    container.appendChild(canvasEl);
    canvasElRef.current = canvasEl;

    const fc = new Canvas(canvasEl, {
      width: window.innerWidth,
      height: window.innerHeight,
      selection: false,
      renderOnAddRemove: true,
    });
    fabricRef.current = fc;

    const handleResize = () => {
      fc.setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      fc.dispose();
      fabricRef.current = null;
      if (canvasEl.parentNode) {
        canvasEl.parentNode.removeChild(canvasEl);
      }
      canvasElRef.current = null;
    };
  }, [canvasElRef]);

  useEffect(() => {
    registerClearHandler(() => {
      const fc = fabricRef.current;
      if (!fc) return;
      fc.clear();
      arrowGroupsRef.current.clear();
      fc.renderAll();
    });
  }, [registerClearHandler]);

  useEffect(() => {
    registerUndoHandler(() => {
      const fc = fabricRef.current;
      if (!fc) return;
      const objects = fc.getObjects();
      if (objects.length === 0) return;
      const last = objects[objects.length - 1];
      if (!last) return;

      const arrowGroup = [...arrowGroupsRef.current.entries()].find(
        ([shaft, heads]) => shaft === last || heads.includes(last),
      );
      if (arrowGroup) {
        const [shaft, heads] = arrowGroup;
        fc.remove(shaft, ...heads);
        arrowGroupsRef.current.delete(shaft);
      } else {
        fc.remove(last);
      }
      fc.renderAll();
    });
  }, [registerUndoHandler]);

  useEffect(() => {
    const fc = fabricRef.current;
    if (!fc) return;

    if (!isActive) {
      fc.isDrawingMode = false;
      fc.selection = false;
      return;
    }

    if (activeTool === 'freehand') {
      fc.isDrawingMode = true;
      const brush = new PencilBrush(fc);
      brush.color = color;
      brush.width = 3;
      fc.freeDrawingBrush = brush;
    } else {
      fc.isDrawingMode = false;
    }
  }, [isActive, activeTool, color]);

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (!isActive || activeTool === 'freehand') return;
      const fc = fabricRef.current;
      if (!fc) return;

      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      isDrawingShapeRef.current = true;
      shapeStartRef.current = { x, y };

      if (activeTool === 'circle') {
        const circle = new Circle({
          left: x,
          top: y,
          radius: 1,
          fill: 'transparent',
          stroke: color,
          strokeWidth: 3,
          selectable: false,
          evented: false,
          originX: 'center',
          originY: 'center',
        });
        fc.add(circle);
        activeShapeRef.current = circle;
      } else if (activeTool === 'arrow') {
        const line = new Line([x, y, x, y], {
          stroke: color,
          strokeWidth: 3,
          selectable: false,
          evented: false,
        });
        fc.add(line);
        activeShapeRef.current = line;
      }
    },
    [isActive, activeTool, color],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDrawingShapeRef.current || !shapeStartRef.current) return;
      const fc = fabricRef.current;
      if (!fc) return;

      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const start = shapeStartRef.current;
      const shape = activeShapeRef.current;
      if (!shape) return;

      if (activeTool === 'circle') {
        const radius = Math.sqrt((x - start.x) ** 2 + (y - start.y) ** 2);
        (shape as Circle).set({ radius });
        fc.renderAll();
      } else if (activeTool === 'arrow') {
        (shape as Line).set({ x2: x, y2: y });
        fc.renderAll();
      }
    },
    [activeTool],
  );

  const handleMouseUp = useCallback(() => {
    if (!isDrawingShapeRef.current) return;
    const fc = fabricRef.current;
    if (!fc) return;

    if (
      activeTool === 'arrow' &&
      activeShapeRef.current &&
      shapeStartRef.current
    ) {
      const shaft = activeShapeRef.current;
      const line = shaft as Line;
      const x1 = line.x1 ?? 0;
      const y1 = line.y1 ?? 0;
      const x2 = line.x2 ?? 0;
      const y2 = line.y2 ?? 0;
      const heads = addArrowHead(fc, x1, y1, x2, y2, color);
      arrowGroupsRef.current.set(shaft, heads);
    }

    isDrawingShapeRef.current = false;
    shapeStartRef.current = null;
    activeShapeRef.current = null;
    fc.renderAll();
  }, [activeTool, color]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isActive || activeTool === 'freehand') return;

    container.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isActive, activeTool, handleMouseDown, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (!isActive) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActive(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, setActive]);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0"
      style={{
        zIndex: 40,
        pointerEvents: isActive ? 'auto' : 'none',
      }}
      data-capture-include-annotations
    />
  );
}

function addArrowHead(
  fc: Canvas,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  arrowColor: string,
): FabricObject[] {
  const headLen = 15;
  const angle = Math.atan2(y2 - y1, x2 - x1);

  const head1 = new Line(
    [
      x2,
      y2,
      x2 - headLen * Math.cos(angle - Math.PI / 6),
      y2 - headLen * Math.sin(angle - Math.PI / 6),
    ],
    {
      stroke: arrowColor,
      strokeWidth: 3,
      selectable: false,
      evented: false,
    },
  );
  const head2 = new Line(
    [
      x2,
      y2,
      x2 - headLen * Math.cos(angle + Math.PI / 6),
      y2 - headLen * Math.sin(angle + Math.PI / 6),
    ],
    {
      stroke: arrowColor,
      strokeWidth: 3,
      selectable: false,
      evented: false,
    },
  );
  fc.add(head1);
  fc.add(head2);
  return [head1, head2];
}
